/**
 * A question the product's variation answers ("Boja kutije").
 *
 * One template can serve every colour of a product when only a few layers
 * differ: a hidden choice question names a WooCommerce attribute
 * (`config.fromAttribute: 'boja-kutije'`), its choices are the attribute's
 * values, and each choice's bindings swap a picture or a colour. The shopper
 * never sees it — the variation they picked is the answer — and the server sets
 * the same answer from the variation the order was bought as, so the print can
 * never disagree with the box.
 *
 * Six Mini Box templates that differed only in their hearts became one: the
 * page loads one design, and a colour click is instant (nothing to reload,
 * every photo and quote stays).
 */

/** The attribute a question takes its answer from, or undefined. Stored without `attribute_`/`pa_`. */
export function fromAttributeOf(option: { config?: Record<string, unknown> | null }): string | undefined {
  const raw = option.config?.fromAttribute;
  if (typeof raw !== 'string') return undefined;
  const bare = bareAttributeName(raw);
  return bare === '' ? undefined : bare;
}

/** `attribute_pa_boja-kutije` → `boja-kutije`, as WooCommerce names the same attribute three ways. */
export function bareAttributeName(name: string): string {
  return name.trim().replace(/^attribute_/i, '').replace(/^pa_/i, '').toLowerCase();
}

/** "Plavo Bela", "plavo-bela" and "PLAVO BELA" are one value (a global attribute's slug, a local one's name). */
export function sameAttributeValue(a: string, b: string): boolean {
  const norm = (s: string): string => s.trim().toLowerCase().replace(/[\s_]+/g, '-');
  return norm(a) === norm(b);
}

/**
 * The choice (its stored `value`) the variation picks for a question that takes
 * its answer from an attribute; undefined when the question does not, the
 * variation does not name the attribute, or no choice matches its value.
 * A choice matches by its value or its label.
 */
export function variationAnswer(
  option: { config?: Record<string, unknown> | null; values?: ReadonlyArray<{ value: string; label?: string | null }> | null },
  attributes: Record<string, string> | null | undefined,
): string | undefined {
  const attribute = fromAttributeOf(option);
  if (!attribute || !attributes) return undefined;
  let picked: string | undefined;
  for (const [key, value] of Object.entries(attributes)) {
    if (bareAttributeName(key) === attribute && typeof value === 'string' && value.trim() !== '') picked = value;
  }
  if (picked === undefined) return undefined;
  const choice = (option.values ?? []).find(
    (v) => sameAttributeValue(v.value, picked!) || (typeof v.label === 'string' && sameAttributeValue(v.label, picked!)),
  );
  return choice?.value;
}

/** Option types whose answer is one of their choices: the only ones a variation can answer. */
export const VARIATION_ANSWER_TYPES: readonly string[] = ['dropdown', 'swatch'];
